import React, { useCallback } from 'react';
import {
  MainLabelContent,
  LabelAndSubLabelContainer,
  Label,
  FieldDataExpander,
} from './atoms';
import {
  LabelsColumnListLinkModernized,
  CollapsibleLinkArgs,
} from './LabelsColumnListLinkModernized';
import { GroupExpanderModernized } from './GroupExpanderModernized';
import { ItemLabelStylesWrapper } from './ItemLabelStylesWrapper';
import { ColorIndicator } from './ColorIndicator';
import { ComponentIcon } from './ComponentIcon';
import { GroupSubLabel } from './GroupSubLabel';
import { OtherLabels } from './OtherLabels';
import { classes } from '@ardoq/common-helpers';
import {
  TimelineLabelWithRenderingParameters,
  TimelineViewCommands,
} from '../../types';
import { POPOVER_ELEMENT_TYPE } from '../../consts';

type ModernizedLabelProps = {
  itemLabelData: TimelineLabelWithRenderingParameters;
  labelsColumnListLinkProps: CollapsibleLinkArgs;
  hasGrouping: boolean;
  labelContainerProps: {
    onMouseEnter: () => void;
    onMouseLeave?: () => void;
  };
  toggleCollapseExpandGroup: (e: React.MouseEvent<HTMLDivElement>) => void;
  viewInstanceId: string;
  commands: TimelineViewCommands;
};
export const ModernizedLabelLayout = ({
  itemLabelData,
  labelsColumnListLinkProps,
  hasGrouping,
  labelContainerProps,
  toggleCollapseExpandGroup,
  viewInstanceId,
  commands,
}: ModernizedLabelProps) => {
  const {
    itemModel: {
      id: graphNodeId,
      rawItemIdPath,
      itemClass,
      isGroup,
      excluded,
      itemLabels: { mainLabel, subLabel },
      isFieldDataCollapsible,
    },
    isFieldDataCollapsed,
    componentRepresentationData,
    contrastedComponentColor,
    isComponentHierarchyCollapsed,
    labelContentStyle,
    colorIndicatorColor,
    otherLabelsRenderingData,
    otherLabelsContainerStyle,
  } = itemLabelData;

  const expandCollapseFieldData = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      commands.collapseExpandFieldData(
        rawItemIdPath,
        !isFieldDataCollapsed,
        viewInstanceId
      );
    },
    [rawItemIdPath, isFieldDataCollapsed, viewInstanceId, commands]
  );

  const shouldShowOtherLabels =
    !isFieldDataCollapsed &&
    otherLabelsRenderingData &&
    otherLabelsRenderingData.labelsData.length > 0;
  return (
    <>
      {hasGrouping && (
        <LabelsColumnListLinkModernized {...labelsColumnListLinkProps} />
      )}
      {isGroup && rawItemIdPath && (
        <GroupExpanderModernized
          onClick={toggleCollapseExpandGroup}
          isCollapsed={isComponentHierarchyCollapsed}
        />
      )}

      <ItemLabelStylesWrapper
        isGroupLabel={hasGrouping && isGroup}
        excluded={excluded}
        labelContainerProps={labelContainerProps}
        withOtherLabels
        style={labelContentStyle}
      >
        <MainLabelContent>
          <ColorIndicator
            isGroup={hasGrouping && isGroup}
            color={colorIndicatorColor}
          />
          {componentRepresentationData && (
            <ComponentIcon
              color={contrastedComponentColor}
              itemClass={itemClass}
              componentRepresentationData={componentRepresentationData}
            />
          )}
          <LabelAndSubLabelContainer
            data-graph-node-id={graphNodeId}
            data-popover-element-type={POPOVER_ELEMENT_TYPE.MAIN_LABEL}
          >
            <Label
              className={classes('component-name', 'component')}
              $shouldEllipsis
            >
              {mainLabel}
            </Label>
            {subLabel && <GroupSubLabel groupSublabel={subLabel} />}
          </LabelAndSubLabelContainer>
          {isFieldDataCollapsible && (
            <FieldDataExpander
              direction={isFieldDataCollapsed ? 'down' : 'up'}
              deprecated_onClick={expandCollapseFieldData}
            />
          )}
        </MainLabelContent>
        {shouldShowOtherLabels && (
          <OtherLabels
            graphNodeId={graphNodeId}
            otherLabelsRenderingData={otherLabelsRenderingData}
            otherLabelsContainerStyle={otherLabelsContainerStyle}
          />
        )}
      </ItemLabelStylesWrapper>
    </>
  );
};
